import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';

import Button from '../../../components/Button';
import ContentCard from '../../../components/ContentCard';
import ContentWrapper from '../../../components/ContentWrapper';
import DataGrid from '../../../components/DataGrid';
import Modal from '../../../components/Modal';
import { NoPermissionMessage } from '../../../components/PageLayout';
import SearchField from '../../../components/SearchField';
import SkeletonStack from '../../../components/SkeletonStack';
import StudentListItem from '../../../components/StudentListItem';
import Tabs from '../../../components/Tabs';
import { getCurrentUserId } from '../../../services/auth';
import {
  useDeleteExamAttemptMutation, useGetExamAttemptsByUserQuery, useGetExamsQuery,
} from '../../../services/exams';
import { useGetStudentsQuery } from '../../../services/students';
import { PermissionType } from '../../../types/common';
import { SubjectDetails } from '../../../utils/common/constants';
import getSubjectsFromPermissions from '../../../utils/common/getSubjectsFromPermissions';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import useTab from '../../../utils/react/hooks/useTab';
import useUserPermissions from '../../../utils/react/hooks/useUserPermissions';
import {
  ContentContainer, InProgressDateText, RootButtonsContainer,
} from '../index.styles';

type DeleteTarget = {
  student: string;
  studentName: string;
  subject: string;
  examId: string;
  examLabel: string;
  attemptNumber: number;
}

const LegacyMarkingPage = () => {
  const userId = getCurrentUserId();
  const permissions = useUserPermissions();

  const [subjectsList, setSubjectsList] = useState<SubjectDetails[] | null>(
    permissions ? getSubjectsFromPermissions(permissions, PermissionType.Exam) : null,
  );
  const [studentSearch, setStudentSearch] = useState('');
  const [examSearch, setExamSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<string | undefined>();
  const [selectedExam, setSelectedExam] = useState<string | undefined>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | undefined>();

  const [tab, setTab] = useTab({
    default: subjectsList?.[0]?.key || '',
    key: 'subject',
  });

  /**
   * Fetching
   */
  const { data: examOpts, isLoading: isExamsLoading } = useGetExamsQuery({ subject: tab }, { skip: !tab || tab === '' });
  const { data: markedExams } = useGetExamAttemptsByUserQuery(
    { marker: userId, subject: tab }, { skip: !userId },
  );

  const [deleteAttempt, {
    isLoading: isDeleteAttemptLoading,
    isSuccess: isDeleteAttemptSuccess,
    isError: isDeleteAttemptError,
  }] = useDeleteExamAttemptMutation();

  const {
    data: students,
    isLoading: isStudentQueryLoading,
    refetch,
  } = useGetStudentsQuery({ search: studentSearch });

  useEffect(() => {
    refetch();
  }, [studentSearch]);

  /**
   * Modal Handling
   */

  useEffect(() => {
    if (deleteTarget) {
      setIsDeleteModalOpen(true);
    }
  }, [deleteTarget]);

  useEffect(() => {
    if (!isDeleteModalOpen) {
      // Clear delete target with a delay to allow for animation
      setTimeout(() => setDeleteTarget(undefined), 200);
    }
  }, [isDeleteModalOpen]);

  /**
   * Error handling
   */
  useEffect(() => {
    if (isDeleteAttemptSuccess || isDeleteAttemptError) {
      // Success or fail, close the modal
      setIsDeleteModalOpen(false);
    }
  }, [isDeleteAttemptSuccess, isDeleteAttemptError]);

  useSuccess(isDeleteAttemptSuccess, 'Deleted attempt');
  useError(isDeleteAttemptError, 'Failed to delete attempt');

  /**
   * On Load
   */
  useEffect(() => {
    const updateSubjects = async () => {
      if (permissions) {
        const list = getSubjectsFromPermissions(permissions, PermissionType.Exam);
        setSubjectsList(list);
      }
    };
    updateSubjects();
  }, [permissions]);

  if (!subjectsList || !permissions) {
    return (
      <SkeletonStack />
    );
  }

  if (subjectsList && subjectsList?.length === 0) {
    // If subjectsList has loaded but there are no subjects, show no permission message
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <>
      <Tabs
        options={subjectsList || []}
        selectedKey={tab}
        onChange={(key) => setTab(key)}
      />
      <ContentContainer>
        <ContentCard variant="outlined">
          <ContentWrapper title="Student">
            <SearchField
              type="dropdown"
              placeholder="Select Student"
              options={students?.data ?? []}
              getOptionKey={(a) => a?.id}
              getOptionLabel={(a) => a?.name}
              onSearch={(str) => setStudentSearch(str)}
              loading={isStudentQueryLoading}
              onSelect={(a) => {
                setSelectedStudent(a?.id);
              }}
              renderOption={(props, s) => (
                <StudentListItem
                  student={s}
                  {...props}
                  key={s.id}
                />
              )}
            />
          </ContentWrapper>
          <ContentWrapper title="Exam">
            <SearchField
              type="dropdown"
              placeholder="Select Exam"
              options={examOpts?.data.filter((exam) => exam.label.match(examSearch)) ?? []}
              getOptionKey={(a) => a?.id}
              getOptionLabel={(a) => a?.label}
              onSearch={(str) => setExamSearch(str)}
              loading={isExamsLoading}
              onSelect={(a) => {
                setSelectedExam(a?.id);
              }}
            />
          </ContentWrapper>
          <RootButtonsContainer>
            <Button
              disabled={!(selectedExam && selectedStudent)}
              // Default to attempt 1 for now, should update once attempts are measured
              href={`${tab}/${selectedExam}/${selectedStudent}/1`}
            >
              Mark Paper
            </Button>
            <Button
              disabled={!selectedStudent || !!selectedExam}
              href={`/analytics/${selectedStudent}`}
            >
              View Analytics
            </Button>
          </RootButtonsContainer>
        </ContentCard>
        <ContentCard variant="outlined" title="Marked Exams">
          <DataGrid
            rows={markedExams?.data || []}
            initialState={{
              sorting: {
                sortModel: [{ field: 'markedDatetime', sort: 'desc' }],
              },
            }}
            columns={[
              {
                field: 'markedDatetime',
                headerName: 'Date',
                width: 120,
                renderCell: (e: any) => (e.value === Infinity
                  ? <InProgressDateText>In Progress</InProgressDateText>
                  : dayjs(e.value).format('D-MMM')),
              },
              {
                field: 'studentName',
                headerName: 'Student',
                width: 160,
              },
              {
                field: 'examLabel',
                headerName: 'Exam',
                width: 240,
              },
              {
                field: 'control',
                headerName: 'Control',
                width: 100,
                renderCell: ({ row }: any) => (
                  <Button
                    color="error"
                    size="small"
                    variant="text"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      setDeleteTarget({
                        student: row.student,
                        studentName: row.studentName,
                        examId: row.examId,
                        examLabel: row.examLabel,
                        attemptNumber: row.attemptNumber,
                        subject: row.subject,
                      });
                    }}
                  >
                    Delete
                  </Button>
                ),
              },
            ]}
            getRowId={(e) => `${e.student}#${e.examId}#${e.attemptNumber}`}
            getRowHref={(row: any) => `${tab}/${row.examId}/${row.student}/${row.attemptNumber}`}
          />
        </ContentCard>
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Are you sure?"
          description={`This will permanently delete ${deleteTarget?.studentName}'s attempt of ${deleteTarget?.examLabel} and all analytics data associated with the attempt.`}
          buttons={[
            {
              label: 'Delete',
              color: 'error',
              loading: isDeleteAttemptLoading,
              onClick: async () => {
                if (deleteTarget) {
                  deleteAttempt({
                    subject: deleteTarget?.subject,
                    examId: deleteTarget?.examId,
                    studentId: deleteTarget?.student,
                    attemptNum: deleteTarget?.attemptNumber,
                  });
                }
              },
            },
          ]}
        />
      </ContentContainer>
    </>
  );
};

export default LegacyMarkingPage;
